/**
 * Full WebGIS suite — AppShell + map always mounted.
 * Simple mode = guided overlay on the same live map (Phase 3 unified shell).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Collapse,
  alpha,
  Chip,
  Fab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LayersIcon from '@mui/icons-material/Layers';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import KeyIcon from '@mui/icons-material/Key';
import MapIcon from '@mui/icons-material/Map';
import ThreeDRotation from '@mui/icons-material/ThreeDRotation';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import ShareIcon from '@mui/icons-material/Share';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from './shell/AppShell';
import MapComponent from './Map';
import BasemapSelector from './BasemapSelector';
import IdentifyResultsPanel from './IdentifyResultsPanel';
import DataSourceManager from './DataSourceManager';
import ProjectDialog from './ProjectDialog';
import AttributeTable from './AttributeTable';
import TextAnnotationDialog from './TextAnnotationDialog';
import SimpleModeOverlay from './SimpleModeOverlay';
import SimpleModeToolHud from './SimpleModeToolHud';
import ThreeScene from './ThreeScene';
import PropertyCardWizard from './propertyCard/PropertyCardWizard';
import ShareMapDialog from './ShareMapDialog';
import PrintDialog from './PrintDialog';
import GuestSavePrompt from './GuestSavePrompt';
import { peekPendingShareIntent } from '../services/shareMapService';
import { useGeoveraTheme } from '../theme/ThemeContext';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setMeasurementMode } from '../store/slices/measurementSlice';
import {
  openPrintDialog,
  closePrintDialog,
  setExporting,
  setExportError,
} from '../store/slices/printExportSlice';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';
import { setIdentifyMode, addNotification, setNonTechnicalMode } from '../store/slices/uiSlice';
import { addLayer } from '../store/slices/layersSlice';
import { DEMO_PARCEL_LAYER } from '../utils/demoSampleParcel';
import { mapNavigator } from '../services/MapNavigator';
import { generatePDF, downloadBlob } from '../services/PrintService';

const WELCOME_KEY = 'geovera-webgis-welcome-dismissed';

const WebGISWorkspace = () => {
  const { palette } = useGeoveraTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const layers = useAppSelector((s) => s.layers.layers);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);
  const identifyMode = useAppSelector((s) => s.ui.identifyMode);
  const isSimple = useAppSelector((s) => s.ui.isNonTechnicalMode);
  const printOptions = useAppSelector((s) => s.printExport.printOptions);

  const [identifyOpen, setIdentifyOpen] = useState(false);
  const [dataSourceOpen, setDataSourceOpen] = useState(false);
  const [attributeOpen, setAttributeOpen] = useState(false);
  const [basemapOpen, setBasemapOpen] = useState(false);
  const [projectDialog, setProjectDialog] = useState<{
    open: boolean;
    mode: 'open' | 'save';
  }>({ open: false, mode: 'open' });
  const [showWelcome, setShowWelcome] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const [simpleGuideVisible, setSimpleGuideVisible] = useState(true);
  const [propertyCardOpen, setPropertyCardOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareAutoPublish, setShareAutoPublish] = useState(false);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isRestoring = useAppSelector((s) => s.auth.isRestoring);
  const demoParcelInitialized = useRef(false);

  // When entering simple mode, show guide again
  useEffect(() => {
    if (isSimple) setSimpleGuideVisible(true);
  }, [isSimple]);

  // Auto-populate demo parcel layer on initial workspace load if empty
  useEffect(() => {
    if (!demoParcelInitialized.current) {
      demoParcelInitialized.current = true;
      if (layers.length === 0) {
        dispatch(addLayer(DEMO_PARCEL_LAYER));
      }
    }
  }, [layers.length, dispatch]);

  // Resume share after login: ?share=1 or pending session intent
  useEffect(() => {
    if (isRestoring) return;
    const shareParam = searchParams.get('share') === '1';
    const pending = peekPendingShareIntent();
    if (!shareParam && !pending) return;

    // Clear query flag so refresh doesn't re-open forever
    if (shareParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('share');
      setSearchParams(next, { replace: true });
    }

    if (!isAuthenticated) {
      // Keep intent; dialog will prompt sign-in
      setShareAutoPublish(false);
      setShareDialogOpen(true);
      return;
    }

    // Authenticated resume — auto-publish (pending cleared after successful publish)
    setShareAutoPublish(true);
    setShareDialogOpen(true);
    dispatch(
      addNotification({
        type: 'info',
        message: 'Finishing your share link…',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isRestoring]);

  useEffect(() => {
    const welcomeParam = searchParams.get('welcome');
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(WELCOME_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (searchParams.get('mode') === 'simple') {
      dispatch(setNonTechnicalMode(true));
    }
    if (!isSimple && (welcomeParam === '1' || (!dismissed && layers.length === 0))) {
      setShowWelcome(true);
    }
    if (welcomeParam || searchParams.has('mode')) {
      const next = new URLSearchParams(searchParams);
      next.delete('welcome');
      next.delete('mode');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (identifyMode) setIdentifyOpen(true);
  }, [identifyMode]);

  useEffect(() => {
    if (identifyMode && selectedFeatures.length > 0) setIdentifyOpen(true);
  }, [identifyMode, selectedFeatures]);

  useEffect(() => {
    const onOpenImport = () => setDataSourceOpen(true);
    const onOpenProject = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: 'open' | 'save' }>).detail?.mode || 'open';
      setProjectDialog({ open: true, mode });
    };
    const onOpenIdentify = () => {
      dispatch(setIdentifyMode(true));
      setIdentifyOpen(true);
      dispatch(
        addNotification({
          type: 'info',
          message: 'Identify mode — click features on the map',
        })
      );
    };
    const onOpenAttributes = () => setAttributeOpen(true);
    const onToggleBasemap = () => setBasemapOpen((v) => !v);
    const onStartMeasure = () => {
      dispatch(setMeasurementMode('distance'));
      window.dispatchEvent(new CustomEvent('geovera:open-activity', { detail: { id: 'measure' } }));
    };
    const onOpenPrint = () => {
      // Always open print dialog (works in simple mode without Print side panel)
      dispatch(openPrintDialog());
    };
    const onToggle3d = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled?: boolean }>).detail || {};
      const enabled = detail.enabled !== undefined ? !!detail.enabled : undefined;
      setShow3D((prev) => (enabled !== undefined ? enabled : !prev));
    };
    const onOpenShare = () => {
      setShareAutoPublish(false);
      setShareDialogOpen(true);
    };
    const onOpenPropertyCard = () => {
      setPropertyCardOpen(true);
    };
    const onAddNotification = (e: Event) => {
      const detail = (e as CustomEvent<{ type?: string; message?: string }>).detail || {};
      if (!detail.message) return;
      const type = (detail.type as 'success' | 'error' | 'warning' | 'info') || 'info';
      dispatch(addNotification({ type, message: detail.message }));
    };

    window.addEventListener('geovera:open-import', onOpenImport);
    window.addEventListener('geovera:open-project', onOpenProject as EventListener);
    window.addEventListener('geovera:open-identify', onOpenIdentify);
    window.addEventListener('geovera:open-attributes', onOpenAttributes);
    window.addEventListener('geovera:toggle-basemap', onToggleBasemap);
    window.addEventListener('geovera:start-measure', onStartMeasure);
    window.addEventListener('geovera:open-print', onOpenPrint as EventListener);
    window.addEventListener('geovera:toggle-3d', onToggle3d as EventListener);
    window.addEventListener('geovera:add-notification', onAddNotification as EventListener);
    window.addEventListener('geovera:open-share', onOpenShare);
    window.addEventListener('geovera:open-property-card', onOpenPropertyCard);
    return () => {
      window.removeEventListener('geovera:open-import', onOpenImport);
      window.removeEventListener('geovera:open-project', onOpenProject as EventListener);
      window.removeEventListener('geovera:open-identify', onOpenIdentify);
      window.removeEventListener('geovera:open-attributes', onOpenAttributes);
      window.removeEventListener('geovera:toggle-basemap', onToggleBasemap);
      window.removeEventListener('geovera:start-measure', onStartMeasure);
      window.removeEventListener('geovera:open-print', onOpenPrint as EventListener);
      window.removeEventListener('geovera:toggle-3d', onToggle3d as EventListener);
      window.removeEventListener('geovera:add-notification', onAddNotification as EventListener);
      window.removeEventListener('geovera:open-share', onOpenShare);
      window.removeEventListener('geovera:open-property-card', onOpenPropertyCard);
    };
  }, [dispatch]);

  const handlePrintPdf = useCallback(() => {
    const map = mapNavigator.getMap();
    if (!map) {
      dispatch(setExportError('Map is not ready yet. Try again in a moment.'));
      dispatch(addNotification({ type: 'error', message: 'Map not ready for print' }));
      return;
    }
    try {
      dispatch(setExporting(true));
      const options = {
        ...printOptions,
        title: printOptions.title?.trim() || 'GeoVera Map',
        includeTitle: printOptions.includeTitle ?? true,
      };
      const legendLayers = layers
        .filter((l) => l.visible)
        .map((l) => ({
          name: l.name,
          color: '#4fc3f7',
          geometryType: l.geometryType || 'Polygon',
        }));
      const blob = generatePDF(map, options, legendLayers);
      const safeName = (options.title || 'geovera-map').replace(/[^\w-]+/g, '_');
      downloadBlob(blob, `${safeName}.pdf`);
      dispatch(setExporting(false));
      dispatch(closePrintDialog());
      dispatch(addNotification({ type: 'success', message: 'PDF downloaded' }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Print failed';
      dispatch(setExportError(msg));
      dispatch(setExporting(false));
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [dispatch, layers, printOptions]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
    return () => window.clearTimeout(t);
  }, []);

  // Resize map when 3D panel opens/closes
  useEffect(() => {
    const t = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 150);
    return () => window.clearTimeout(t);
  }, [show3D]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    try {
      localStorage.setItem(WELCOME_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  // Features for 3D extrusion (selected or first vector layer sample)
  const features3d =
    selectedFeatures.length > 0
      ? selectedFeatures
      : layers.find((l) => l.type === 'vector' && l.features?.length)?.features?.slice(0, 50) || [];

  const handleShareMap = useCallback(() => {
    setShareAutoPublish(false);
    setShareDialogOpen(true);
  }, []);

  const handlePropertyCard = useCallback(() => {
    setPropertyCardOpen(true);
  }, []);

  return (
    <AppShell initialActivity={isSimple ? null : 'layers'}>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
          <MapComponent useReduxState={true} />

          {/* Simple mode guided cards — map stays live underneath */}
          {isSimple && simpleGuideVisible && (
            <SimpleModeOverlay
              onHide={() => setSimpleGuideVisible(false)}
              onRequestAdvanced={() => dispatch(setNonTechnicalMode(false))}
            />
          )}

          {/* Reopen simple guide */}
          {isSimple && !simpleGuideVisible && (
            <Fab
              size="small"
              color="secondary"
              variant="extended"
              onClick={() => setSimpleGuideVisible(true)}
              sx={{
                position: 'absolute',
                left: 12,
                top: 12,
                zIndex: 40,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              <AutoAwesome sx={{ mr: 0.75, fontSize: 18 }} />
              Simple guide
            </Fab>
          )}

          {/* Measure / draw HUD when side panels are hidden in simple mode */}
          {isSimple && <SimpleModeToolHud />}

          {/* Floating map utilities */}
          <Stack
            direction="column"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 20,
            }}
          >
            <Paper
              elevation={4}
              sx={{
                bgcolor: alpha(palette.bg.elevated, 0.94),
                border: '1px solid rgba(56, 189, 248, 0.45)',
                borderRadius: 2,
              }}
            >
              <Button
                size="small"
                startIcon={<BadgeIcon sx={{ fontSize: 16 }} />}
                onClick={handlePropertyCard}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#38bdf8',
                  px: 1.5,
                  py: 0.75,
                }}
              >
                Property card
              </Button>
            </Paper>
            <Paper
              elevation={4}
              sx={{
                bgcolor: alpha(palette.bg.elevated, 0.94),
                border: '1px solid rgba(132, 204, 22, 0.4)',
                borderRadius: 2,
              }}
            >
              <Button
                size="small"
                startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                onClick={handleShareMap}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#84cc16',
                  px: 1.5,
                  py: 0.75,
                }}
              >
                Share link
              </Button>
            </Paper>
            <Paper
              elevation={4}
              sx={{
                bgcolor: alpha(palette.bg.elevated, 0.94),
                border: `1px solid ${palette.border.base}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Button
                size="small"
                startIcon={<MapIcon sx={{ fontSize: 16 }} />}
                onClick={() => setBasemapOpen((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: palette.text.primary,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 0,
                }}
              >
                Basemap
              </Button>
              <Collapse in={basemapOpen}>
                <Box sx={{ p: 1, maxWidth: 280, maxHeight: 320, overflow: 'auto' }}>
                  <BasemapSelector mode="compact" />
                </Box>
              </Collapse>
            </Paper>
            <Paper
              elevation={4}
              sx={{ bgcolor: alpha(palette.bg.elevated, 0.94), borderRadius: 2 }}
            >
              <Button
                size="small"
                startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                onClick={() => setDataSourceOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: palette.text.primary,
                  px: 1.5,
                }}
              >
                Add data
              </Button>
            </Paper>
            <Paper
              elevation={4}
              sx={{ bgcolor: alpha(palette.bg.elevated, 0.94), borderRadius: 2 }}
            >
              <Button
                size="small"
                startIcon={<LayersIcon sx={{ fontSize: 16 }} />}
                onClick={() => setAttributeOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: palette.text.primary,
                  px: 1.5,
                }}
              >
                Attributes
              </Button>
            </Paper>
            <Paper
              elevation={4}
              sx={{ bgcolor: alpha(palette.bg.elevated, 0.94), borderRadius: 2 }}
            >
              <Button
                size="small"
                startIcon={<ThreeDRotation sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setShow3D((v) => !v);
                  window.dispatchEvent(
                    new CustomEvent('geovera:toggle-3d', {
                      detail: { enabled: !show3D },
                    })
                  );
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: show3D ? palette.accent.primary : palette.text.primary,
                  px: 1.5,
                }}
              >
                {show3D ? 'Hide 3D' : '3D'}
              </Button>
            </Paper>
          </Stack>

          {/* Advanced-mode welcome coach */}
          <Collapse in={showWelcome && !isSimple}>
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                left: 16,
                bottom: 16,
                zIndex: 30,
                maxWidth: 400,
                p: 2.5,
                bgcolor: alpha(palette.bg.elevated, 0.97),
                border: `1px solid ${palette.border.base}`,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: palette.text.primary }}
                >
                  Welcome to GeoVera WebGIS
                </Typography>
                <IconButton size="small" onClick={dismissWelcome} aria-label="Dismiss welcome">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: palette.text.secondary, mb: 2, lineHeight: 1.55 }}
              >
                Header tools and the left activity bar open Layers, Draw, Measure, Analysis, and
                more. Try <strong>Add data</strong> or switch to <strong>Simple mode</strong> for
                guided tasks.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => {
                    setDataSourceOpen(true);
                    dismissWelcome();
                  }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Add data
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={() => {
                    dismissWelcome();
                    dispatch(setNonTechnicalMode(true));
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Simple mode
                </Button>
                <Button
                  size="small"
                  startIcon={<KeyIcon />}
                  onClick={() => {
                    dismissWelcome();
                    navigate('/developers/digipin');
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  API key
                </Button>
              </Stack>
            </Paper>
          </Collapse>

          <IdentifyResultsPanel
            open={identifyOpen}
            identifyMode={identifyMode}
            onClose={() => {
              setIdentifyOpen(false);
              dispatch(setIdentifyMode(false));
            }}
          />

          <DataSourceManager open={dataSourceOpen} onClose={() => setDataSourceOpen(false)} />

          <ProjectDialog
            open={projectDialog.open}
            mode={projectDialog.mode}
            onClose={() => setProjectDialog({ open: false, mode: projectDialog.mode })}
          />

          <AttributeTable open={attributeOpen} onClose={() => setAttributeOpen(false)} />
          <TextAnnotationDialog />

          {/* Print works in simple + advanced (not only when Print side panel is mounted) */}
          <PrintDialog onPrint={handlePrintPdf} />
        </Box>

        {/* 3D dock under map */}
        {show3D && (
          <Paper
            elevation={6}
            sx={{
              height: { xs: 220, sm: 280 },
              flexShrink: 0,
              borderTop: `1px solid ${palette.border.base}`,
              bgcolor: palette.bg.elevated,
              position: 'relative',
              zIndex: 15,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderBottom: `1px solid ${palette.border.subtle}`,
              }}
            >
              <Chip
                size="small"
                icon={<ThreeDRotation sx={{ fontSize: 16 }} />}
                label="3D preview"
                sx={{ fontWeight: 700 }}
              />
              <Typography variant="caption" sx={{ ml: 1.5, color: palette.text.muted, flex: 1 }}>
                Drag to rotate · scroll to zoom
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setShow3D(false);
                  window.dispatchEvent(
                    new CustomEvent('geovera:toggle-3d', { detail: { enabled: false } })
                  );
                }}
                aria-label="Close 3D"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ height: 'calc(100% - 36px)', width: '100%' }}>
              <ThreeScene features={features3d} />
            </Box>
          </Paper>
        )}

        <PropertyCardWizard open={propertyCardOpen} onClose={() => setPropertyCardOpen(false)} />
        <ShareMapDialog
          open={shareDialogOpen}
          autoPublish={shareAutoPublish}
          onClose={() => {
            setShareDialogOpen(false);
            setShareAutoPublish(false);
          }}
        />
        <GuestSavePrompt />
      </Box>
    </AppShell>
  );
};

export default WebGISWorkspace;
